import * as SunCalc from 'suncalc';
import store from '../../../../state';
import { getLocalDateTimeParts } from '../../../../services/PoiDataService';
import { ForecastBinaryService } from '../../../../services/ForecastBinaryService';
import TierAccessService from '../../../../services/TierAccessService';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';

export interface DailySummary {
  dayLabel: string;
  dateSub: string;
  icon: string;
  condition: string;
  high: number;
  low: number;
  rainPct: number;
  windSpeed: number;
  windDirDeg: number;
  windDirText: string;
}

export interface LocalCalendarDayBucket {
  ymd: string;
  dayLabel: string;
  dateSub: string;
  hourIndices: number[];
}

export function buildLocalCalendarDayBuckets(ctx: WidgetRenderContext, overrideDays?: number | string): LocalCalendarDayBucket[] {
  const data = ctx.data;
  if (!data || !data.tempsForecast || data.tempsForecast.length === 0) {
    return [];
  }

  const temps = data.tempsForecast;
  const tz = data.timezone || 'UTC';
  const baseMs = typeof data.timelineStartMs === 'number' && !isNaN(data.timelineStartMs)
    ? data.timelineStartMs
    : Math.floor(Date.now() / 3600000) * 3600000;
  const todayYmd = getLocalDateTimeParts(Date.now(), tz).ymd;

  const rawHorizon = overrideDays ?? ctx.viewDays ?? 5;
  const strH = String(rawHorizon).trim().toLowerCase();
  let targetDays = 5;
  if (strH === 'max') {
    targetDays = 15;
  } else if (strH === '10') {
    targetDays = 10;
  } else {
    const m = strH.match(/^(\d+)/);
    if (m) {
      targetDays = Math.max(1, Math.min(15, parseInt(m[1], 10)));
    } else if (!isNaN(Number(rawHorizon))) {
      targetDays = Math.max(1, Math.min(15, Math.round(Number(rawHorizon))));
    }
  }

  if (targetDays === 1) {
    const firstLp = getLocalDateTimeParts(baseMs, tz);
    const count = Math.min(24, temps.length);
    const hourIndices: number[] = [];
    for (let h = 0; h < count; h++) {
      hourIndices.push(h);
    }
    return [{
      ymd: firstLp.ymd,
      dayLabel: firstLp.ymd === todayYmd ? 'Today' : firstLp.weekdayShort,
      dateSub: `${firstLp.weekdayShort} ${firstLp.month}/${firstLp.day}`,
      hourIndices
    }];
  }

  const buckets: LocalCalendarDayBucket[] = [];
  const bucketMap = new Map<string, LocalCalendarDayBucket>();

  for (let h = 0; h < temps.length; h++) {
    const stepMs = baseMs + h * 3600 * 1000;
    const lp = getLocalDateTimeParts(stepMs, tz);
    let bucket = bucketMap.get(lp.ymd);
    if (!bucket) {
      const isFirstToday = buckets.length === 0 && lp.ymd === todayYmd;
      const dayLabel = isFirstToday ? 'Today' : lp.weekdayShort;
      const dateSub = isFirstToday ? `${lp.weekdayShort} ${lp.month}/${lp.day}` : `${lp.month}/${lp.day}`;
      bucket = {
        ymd: lp.ymd,
        dayLabel,
        dateSub,
        hourIndices: []
      };
      bucketMap.set(lp.ymd, bucket);
      buckets.push(bucket);
    }
    bucket.hourIndices.push(h);
  }

  if (buckets.length > 1 && (strH === 'max' || buckets.length > targetDays) && buckets[buckets.length - 1].hourIndices.length < 6) {
    buckets.pop();
  }

  return buckets.slice(0, targetDays);
}

export function degreesToCompass(deg: number): string {
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(((deg % 360) + 360) % 360 / 22.5) % 16;
  return directions[index];
}

export function aggregateDailyForecast(ctx: WidgetRenderContext, overrideDays?: number | string): DailySummary[] {
  const data = ctx.data;
  if (!data || !data.tempsForecast || data.tempsForecast.length === 0) {
    return [];
  }

  const isImperial = store.getState().units === 'imperial';
  const temps = data.tempsForecast;
  const precip = data.precipLikelihood || [];
  const rainRate = data.precipRate || [];
  const clouds = data.cloudCover || [];
  const winds = data.windSpeed || [];
  const windDirs = data.windDir || [];

  const buckets = buildLocalCalendarDayBuckets(ctx, overrideDays);
  const summaries: DailySummary[] = [];

  for (const bucket of buckets) {
    if (bucket.hourIndices.length === 0) continue;

    const sliceTemps = bucket.hourIndices.map(i => temps[i]).filter((v): v is number => v !== undefined);
    if (sliceTemps.length === 0) continue;
    const slicePrecip = bucket.hourIndices.map(i => precip[i] ?? 0);
    const sliceRate = bucket.hourIndices.map(i => rainRate[i] ?? 0);
    const sliceClouds = bucket.hourIndices.map(i => clouds[i] ?? 50);
    const sliceWinds = bucket.hourIndices.map(i => winds[i] ?? 0);
    const sliceDirs = bucket.hourIndices.map(i => windDirs[i] ?? 0);

    const high = Math.round(Math.max(...sliceTemps));
    const low = Math.round(Math.min(...sliceTemps));
    const maxRain = slicePrecip.length ? Math.round(Math.max(...slicePrecip)) : 0;
    const maxRate = sliceRate.length ? Math.max(...sliceRate) : 0;
    const maxWind = sliceWinds.length ? Math.round(Math.max(...sliceWinds)) : 0;

    let peakWindIdx = 0;
    let peakWindVal = -1;
    sliceWinds.forEach((w, idx) => {
      if (w > peakWindVal) {
        peakWindVal = w;
        peakWindIdx = idx;
      }
    });
    const windDirDeg = sliceDirs[peakWindIdx] !== undefined ? sliceDirs[peakWindIdx] : 0;
    const windDirText = degreesToCompass(windDirDeg);

    const avgCloud = sliceClouds.length ? (sliceClouds.reduce((a, b) => a + b, 0) / sliceClouds.length) : 50;

    let icon = '☀️';
    let condition = 'Sunny';
    if (maxRate > 0.08 || maxRain >= 70) {
      icon = '🌧️';
      condition = 'Rain';
    } else if (maxRate > 0.02 || maxRain >= 40) {
      icon = '🌦️';
      condition = 'Showers';
    } else if (avgCloud >= 75) {
      icon = '☁️';
      condition = 'Overcast';
    } else if (avgCloud >= 35) {
      icon = '⛅';
      condition = 'Partly Cloudy';
    } else {
      icon = '☀️';
      condition = 'Clear';
    }

    summaries.push({
      dayLabel: bucket.dayLabel,
      dateSub: bucket.dateSub,
      icon,
      condition,
      high: isImperial ? high : Math.round((high - 32) * 5 / 9),
      low: isImperial ? low : Math.round((low - 32) * 5 / 9),
      rainPct: maxRain,
      windSpeed: isImperial ? maxWind : Math.round(maxWind * 1.60934),
      windDirDeg,
      windDirText
    });
  }

  return summaries;
}

export function resolveSourceBadge(ctx: WidgetRenderContext, widgetOptionSource?: string): string {
  const effectiveSource = (widgetOptionSource && widgetOptionSource !== 'auto') 
    ? widgetOptionSource 
    : (ctx.activeTabId && ctx.activeTabId !== 'blend' ? ctx.activeTabId : 'auto');

  const req = effectiveSource.toLowerCase();
  if (req === 'auto' || req === 'blend') {
    const lat = ctx.lat;
    const lon = ctx.lon;
    const isCA = lat >= 41.6 && lat <= 83.1 && lon >= -141.0 && lon <= -52.6 && (lat >= 49.0 || (lat >= 42.0 && lon < -79.0));
    const isUS = (lat >= 24.5 && lat <= 49.38 && lon >= -124.8 && lon <= -66.9) ||
                 (lat >= 51.0 && lat <= 71.5 && lon >= -179.0 && lon <= -129.0) ||
                 (lat >= 18.9 && lat <= 22.3 && lon >= -160.3 && lon <= -154.8);
    const source = isUS ? 'NWS' : (isCA ? 'ECCC' : 'ECMWF');
    return `Auto (${source})`;
  }
  const map: Record<string, string> = {
    nws: 'NWS',
    eccc: 'ECCC',
    ecmwf: 'ECMWF',
    icon13: 'ICON-13',
    hrrr: 'HRRR',
    rrfs: 'RRFS',
    rdps: 'RDPS',
    gdps: 'GDPS',
    gfs: 'GFS'
  };
  return map[req] || req.toUpperCase();
}

export interface Hourly3HourPoint {
  timeLabel: string;
  icon: string;
  condition: string;
  temp: number;
  precipPct: number;
  windSpeed: number;
  windDirDeg: number;
}

export function aggregate3HourIntervals(ctx: WidgetRenderContext, dayIdx: number, overrideDays?: number | string): Hourly3HourPoint[] {
  const data = ctx.data;
  if (!data || !data.tempsForecast || data.tempsForecast.length === 0) return [];

  const isImperial = store.getState().units === 'imperial';
  const temps = data.tempsForecast;
  const precip = data.precipLikelihood || [];
  const rainRate = data.precipRate || [];
  const snowRate = data.snowRate || [];
  const clouds = data.cloudCover || [];
  const winds = data.windSpeed || [];
  const windDirs = data.windDir || [];

  const tz = data.timezone || 'UTC';
  const baseMs = typeof data.timelineStartMs === 'number' && !isNaN(data.timelineStartMs)
    ? data.timelineStartMs
    : Math.floor(Date.now() / 3600000) * 3600000;

  const buckets = buildLocalCalendarDayBuckets(ctx, overrideDays);
  const bucket = buckets[dayIdx];
  if (!bucket || bucket.hourIndices.length === 0) return [];

  const sampleIndices: number[] = [];
  if (buckets.length <= 1 && dayIdx === 0) {
    for (let k = 0; k < bucket.hourIndices.length && sampleIndices.length < 8; k += 3) {
      sampleIndices.push(bucket.hourIndices[k]);
    }
  } else {
    const count = bucket.hourIndices.length;
    const step = count <= 8 ? 1 : (count <= 16 ? 2 : 3);
    for (let k = 0; k < count && sampleIndices.length < 8; k += step) {
      sampleIndices.push(bucket.hourIndices[k]);
    }
  }

  const points: Hourly3HourPoint[] = [];

  for (const hIdx of sampleIndices) {
    const stepMs = baseMs + hIdx * 3600 * 1000;
    const lp = getLocalDateTimeParts(stepMs, tz);
    const includeWeekday = buckets.length <= 1 && lp.ymd !== bucket.ymd;

    let timePart: string;
    if (isImperial) {
      const period = lp.hour >= 12 ? 'PM' : 'AM';
      const h12 = lp.hour % 12 === 0 ? 12 : lp.hour % 12;
      timePart = `${h12} ${period}`;
    } else {
      timePart = `${String(lp.hour).padStart(2, '0')}:00`;
    }
    const timeLabel = includeWeekday ? `${lp.weekdayShort} ${timePart}` : timePart;

    const rawTemp = hIdx < temps.length ? temps[hIdx] : (temps[0] || 50);
    const temp = isImperial ? Math.round(rawTemp) : Math.round((rawTemp - 32) * 5 / 9);
    const pop = hIdx < precip.length ? Math.round(precip[hIdx]) : 0;
    const rRate = hIdx < rainRate.length ? rainRate[hIdx] : 0;
    const sRate = hIdx < snowRate.length ? snowRate[hIdx] : 0;
    const cloud = hIdx < clouds.length ? clouds[hIdx] : 40;
    const rawWind = hIdx < winds.length ? winds[hIdx] : 5;
    const windSpeed = isImperial ? Math.round(rawWind) : Math.round(rawWind * 1.60934);
    const windDirDeg = hIdx < windDirs.length ? windDirs[hIdx] : 0;

    let isNight = lp.hour < 6 || lp.hour >= 20;
    try {
      const sPos = SunCalc.getPosition(new Date(stepMs), ctx.lat, ctx.lon);
      if (sPos && typeof sPos.altitude === 'number') {
        isNight = sPos.altitude < 0;
      }
    } catch {
      // Fallback to local hour heuristic
    }

    let icon = '☀️';
    let condition = 'Sunny';
    if (sRate > 0.02) {
      icon = '🌨️';
      condition = 'Snow';
    } else if (rRate > 0.08 || pop >= 65) {
      icon = '🌧️';
      condition = 'Rain';
    } else if (rRate > 0.02 || pop >= 35) {
      icon = '🌦️';
      condition = 'Showers';
    } else if (cloud >= 75) {
      icon = '☁️';
      condition = 'Overcast';
    } else if (cloud >= 40) {
      icon = '⛅';
      condition = 'Partly Cloudy';
    } else if (cloud >= 15) {
      icon = isNight ? '🌤️' : '🌤️';
      condition = isNight ? 'Mostly Clear' : 'Mostly Sunny';
    } else {
      icon = isNight ? '🌙' : '☀️';
      condition = isNight ? 'Clear' : 'Clear';
    }

    points.push({
      timeLabel,
      icon,
      condition,
      temp,
      precipPct: pop,
      windSpeed,
      windDirDeg
    });
  }

  return points;
}

export class DailyForecastListWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Daily Weather Forecast';
    const optPeriod = widget.options?.period || 'auto';
    const requestedHorizon = optPeriod !== 'auto' ? optPeriod : ctx.viewDays;
    const days = aggregateDailyForecast(ctx, requestedHorizon);

    const lat = ctx.lat;
    const lon = ctx.lon;
    const isCA = lat >= 41.6 && lat <= 83.1 && lon >= -141.0 && lon <= -52.6 && (lat >= 49.0 || (lat >= 42.0 && lon < -79.0));
    const isUS = (lat >= 24.5 && lat <= 49.38 && lon >= -124.8 && lon <= -66.9) ||
                 (lat >= 51.0 && lat <= 71.5 && lon >= -179.0 && lon <= -129.0) ||
                 (lat >= 18.9 && lat <= 22.3 && lon >= -160.3 && lon <= -154.8);
    const autoAgency = isUS ? 'NWS' : (isCA ? 'ECCC' : 'ECMWF');

    const activeSourceKey = (widget.options?.dataSource && widget.options.dataSource !== 'auto')
      ? widget.options.dataSource
      : (ctx.activeTabId && ctx.activeTabId !== 'blend' ? ctx.activeTabId : 'auto');

    const sourceBadge = resolveSourceBadge(ctx, activeSourceKey);

    const isImperial = store.getState().units === 'imperial';
    const speedUnit = isImperial ? 'mph' : 'km/h';

    const overallMin = days.length ? Math.min(...days.map(d => d.low)) : 0;
    const overallMax = days.length ? Math.max(...days.map(d => d.high)) : 100;
    const tempRange = Math.max(1, overallMax - overallMin);

    const activeH = String(requestedHorizon).toLowerCase();
    const is3 = activeH.startsWith('3');
    const is5 = activeH.startsWith('5');
    const is7 = activeH.startsWith('7');
    const isMax = activeH === 'max' || activeH === '10';

    const enableHourly = widget.options?.enableHourlyExpansion !== false;

    const rowsHtml = days.map((d, dIdx) => {
      const leftPct = Math.round(((d.low - overallMin) / tempRange) * 100);
      const widthPct = Math.max(8, Math.round(((d.high - d.low) / tempRange) * 100));
      const rainHtml = d.rainPct > 0
        ? `<span style="font-size: 0.72rem; font-weight: 700; color: #00d4ff; display: inline-flex; align-items: center; gap: 2px; font-family: monospace;">💧${d.rainPct}%</span>`
        : `<span style="font-size: 0.72rem; color: #64748b; opacity: 0.4;">--</span>`;

      let trayHtml = '';
      if (enableHourly) {
        const hPoints = aggregate3HourIntervals(ctx, dIdx, requestedHorizon);
        const colsHtml = hPoints.map(p => `
          <div style="background: rgba(15, 23, 42, 0.65); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 6px; padding: 6px 2px; display: flex; flex-direction: column; align-items: center; gap: 3px; text-align: center; min-width: 0;">
            <div style="font-size: 0.62rem; color: #94a3b8; font-weight: 600;">${p.timeLabel}</div>
            <div style="font-size: 1.15rem; margin: 1px 0; line-height: 1;" title="${p.condition}">${p.icon}</div>
            <div style="font-size: 0.78rem; font-weight: 800; color: #ffffff; font-family: monospace;">${p.temp}°</div>
            <div style="font-size: 0.62rem; font-weight: 700; color: #00d4ff; font-family: monospace;">${p.precipPct > 0 ? `💧${p.precipPct}%` : '<span style="color:#64748b; opacity:0.35;">--</span>'}</div>
            <div style="font-size: 0.6rem; color: #cbd5e1; display: flex; align-items: center; justify-content: center; gap: 2px; font-family: monospace;">
              <svg style="width: 8px; height: 8px; transform: rotate(${p.windDirDeg}deg); color: #00d4ff;" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z" /></svg>
              <span>${p.windSpeed}m</span>
            </div>
          </div>
        `).join('');

        trayHtml = `
          <div id="daily-tray-${widget.id}-${dIdx}" class="daily-3h-tray" style="display: none; width: 100%; background: rgba(11, 17, 30, 0.95); border: 1px solid rgba(0, 212, 255, 0.25); border-radius: 8px; margin: 4px 0 8px 0; padding: 8px 6px; grid-template-columns: repeat(${Math.max(1, hPoints.length)}, minmax(0, 1fr)); gap: 4px; box-sizing: border-box;">
            ${colsHtml}
          </div>
        `;
      }

      return `
        <div>
          <div class="daily-forecast-row" data-widget-id="${widget.id}" data-day-idx="${dIdx}" style="display: grid; grid-template-columns: 80px 30px 48px 1fr 70px; align-items: center; gap: 8px; padding: 7px 6px; border-bottom: 1px solid rgba(255, 255, 255, 0.05); font-size: 0.8rem; cursor: ${enableHourly ? 'pointer' : 'default'}; border-radius: 6px; transition: background 0.15s ease;" title="${enableHourly ? 'Click to toggle 3-hour forecast glance' : ''}">
            <div style="font-weight: 700; color: #f8fafc; line-height: 1.2;">
              <div>${d.dayLabel}</div>
              <div style="font-size: 0.65rem; color: #94a3b8; font-weight: 500;">${d.dateSub}</div>
            </div>
            <div style="font-size: 1.25rem; text-align: center; line-height: 1;" title="${d.condition}">${d.icon}</div>
            <div>${rainHtml}</div>
            <div style="display: flex; align-items: center; gap: 6px; min-width: 0;">
              <span style="font-size: 0.75rem; font-weight: 700; color: #94a3b8; font-family: monospace; width: 26px; text-align: right;">${d.low}°</span>
              <div style="flex: 1; height: 5px; background: rgba(255, 255, 255, 0.08); border-radius: 999px; position: relative; overflow: hidden;">
                <div style="position: absolute; top: 0; bottom: 0; left: ${leftPct}%; width: ${widthPct}%; border-radius: 999px; background: linear-gradient(90deg, #38bdf8 0%, #fbbf24 60%, #f43f5e 100%);"></div>
              </div>
              <span style="font-size: 0.75rem; font-weight: 700; color: #f8fafc; font-family: monospace; width: 26px; text-align: left;">${d.high}°</span>
            </div>
            <div style="font-size: 0.72rem; font-weight: 600; color: #cbd5e1; text-align: right; display: flex; align-items: center; justify-content: flex-end; gap: 3px; font-family: monospace;">
              <svg style="width: 12px; height: 12px; transform: rotate(${d.windDirDeg}deg); color: #00d4ff; vertical-align: middle;" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z" />
              </svg>
              <span>${d.windSpeed} ${speedUnit}</span>
            </div>
          </div>
          ${trayHtml}
        </div>
      `;
    }).join('');

    return `
      <div class="metric-card" style="background: linear-gradient(145deg, #13151f 0%, #0d0e15 100%); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; flex-wrap: wrap; gap: 8px;">
          <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
            <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">📅 ${title}</span>
            ${widget.options?.allowSourceSelection !== false ? `
            <div style="display: inline-flex; align-items: center; gap: 4px;">
              <span style="font-size: 0.65rem; color: #94a3b8; font-weight: 600;">SOURCE:</span>
              <select class="daily-source-select" style="background: rgba(0, 212, 255, 0.12); color: #00d4ff; border: 1px solid rgba(0, 212, 255, 0.35); border-radius: 6px; padding: 2px 6px; font-size: 0.7rem; font-weight: 700; cursor: pointer; outline: none;">
                <option value="auto" ${activeSourceKey === 'auto' ? 'selected' : ''}>Auto (${autoAgency})</option>
                <option value="nws" ${activeSourceKey === 'nws' ? 'selected' : ''}>NWS (NOAA)</option>
                <option value="eccc" ${activeSourceKey === 'eccc' ? 'selected' : ''}>ECCC (Canada)</option>
                <option value="ecmwf" ${activeSourceKey === 'ecmwf' ? 'selected' : ''}>ECMWF (IFS)</option>
                <option value="icon13" ${activeSourceKey === 'icon13' ? 'selected' : ''}>ICON-13 (DWD)</option>
                <option value="hrrr" ${activeSourceKey === 'hrrr' ? 'selected' : ''}>HRRR (3km)</option>
                <option value="rrfs" ${activeSourceKey === 'rrfs' ? 'selected' : ''}>RRFS (3km)</option>
                <option value="rdps" ${activeSourceKey === 'rdps' ? 'selected' : ''}>RDPS (10km)</option>
                <option value="gdps" ${activeSourceKey === 'gdps' ? 'selected' : ''}>GDPS (15km)</option>
                <option value="gfs" ${activeSourceKey === 'gfs' ? 'selected' : ''}>GFS (0.25°)</option>
              </select>
            </div>
            ` : `
            <span style="font-size: 0.65rem; padding: 2px 6px; background: rgba(0, 212, 255, 0.12); color: #00d4ff; border-radius: 4px; font-weight: 700; border: 1px solid rgba(0, 212, 255, 0.25); letter-spacing: 0.3px;">${sourceBadge}</span>
            `}
          </div>
          <div class="daily-horizon-pills" style="display: inline-flex; background: rgba(0, 0, 0, 0.45); border-radius: 6px; padding: 2px; border: 1px solid rgba(255, 255, 255, 0.08);">
            <button class="daily-h-btn ${is3 ? 'active' : ''}" data-days="3" style="background: ${is3 ? '#00d4ff' : 'transparent'}; color: ${is3 ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.7rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; cursor: pointer;">3D</button>
            <button class="daily-h-btn ${is5 ? 'active' : ''}" data-days="5" style="background: ${is5 ? '#00d4ff' : 'transparent'}; color: ${is5 ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.7rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; cursor: pointer;">5D</button>
            <button class="daily-h-btn ${is7 ? 'active' : ''}" data-days="7" style="background: ${is7 ? '#00d4ff' : 'transparent'}; color: ${is7 ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.7rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; cursor: pointer;">7D</button>
            <button class="daily-h-btn ${isMax ? 'active' : ''}" data-days="max" style="background: ${isMax ? '#00d4ff' : 'transparent'}; color: ${isMax ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.7rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; cursor: pointer;">Max</button>
          </div>
        </div>
        <div>
          ${rowsHtml || ((ctx.data?.accessDenial || ForecastBinaryService.lastAccessDenial || !TierAccessService.userHasLayerAccess('forecast_point_tiles'))
            ? TierAccessService.buildTrendChartDenialHtml({
                denial: ctx.data?.accessDenial || ForecastBinaryService.lastAccessDenial,
                requiredTier: 'pro',
                featureLabel: `${title} (${activeSourceKey.toUpperCase()})`
              })
            : '<div style="color: #94a3b8; font-size: 0.8rem; text-align: center; padding: 12px;">No daily forecast data available</div>')}
        </div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const container = document.querySelector(`[data-widget-id="${widget.id}"]`) || document;
    const buttons = container.querySelectorAll('.daily-h-btn');
    buttons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const d = (btn as HTMLElement).dataset.days;
        if (d && ctx.onHorizonChange) {
          ctx.onHorizonChange(d === 'max' ? 'max' : parseInt(d, 10));
        }
      });
    });

    const sourceSelect = container.querySelector('.daily-source-select') as HTMLSelectElement;
    if (sourceSelect) {
      sourceSelect.addEventListener('change', (e) => {
        e.stopPropagation();
        const val = sourceSelect.value;
        if (ctx.onSourceChange) {
          ctx.onSourceChange(val);
        }
      });
    }

    const enableHourly = widget.options?.enableHourlyExpansion !== false;
    if (enableHourly) {
      const rows = container.querySelectorAll(`[data-widget-id="${widget.id}"].daily-forecast-row`);
      rows.forEach(r => {
        r.addEventListener('click', (e) => {
          e.stopPropagation();
          const dayIdx = (r as HTMLElement).dataset.dayIdx;
          if (dayIdx === undefined) return;
          const tray = container.querySelector(`#daily-tray-${widget.id}-${dayIdx}`) as HTMLElement;
          if (!tray) return;

          const isOpen = tray.style.display === 'grid';
          // Close all trays in this widget
          container.querySelectorAll(`[id^="daily-tray-${widget.id}-"]`).forEach(t => (t as HTMLElement).style.display = 'none');
          rows.forEach(row => {
            (row as HTMLElement).style.background = 'transparent';
            (row as HTMLElement).style.borderLeft = 'none';
          });

          if (!isOpen) {
            tray.style.display = 'grid';
            (r as HTMLElement).style.background = 'rgba(0, 212, 255, 0.06)';
            (r as HTMLElement).style.borderLeft = '3px solid #00d4ff';
          }
        });
      });
    }
  }
}
